import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
  X, Send, Bold, Italic, Underline, List, ListOrdered, Link2, Palette,
  Eraser, FileText, PenLine, ChevronDown, Minus, Maximize2, Image as ImageIcon,
  Paperclip, Loader2, File as FileIcon, ShieldCheck, ShieldAlert, ShieldX,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCompany } from "../context/CompanyContext";
import { SignatureStudio } from "./SignatureStudio";
import { buildSignatureHtml } from "../lib/signature";

const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#E60000", "#A78BFA", "#f4f4f5"];

const TEMPLATES = [
  {
    id: "demande_elements", label: "Demander des éléments",
    subject: "Éléments nécessaires pour avancer sur votre projet",
    body:
`Bonjour {client},

Afin d'avancer sur votre projet, pourriez-vous me transmettre les éléments suivants :

• 
• 
• 

Dès réception, je reviens vers vous rapidement.

Merci d'avance,`,
  },
  {
    id: "presentation", label: "Email de présentation",
    subject: "Présentation de {entreprise}",
    body:
`Bonjour {client},

Je me permets de vous contacter pour vous présenter {entreprise} et la façon dont nous pouvons vous accompagner.

Nous proposons :
• 
• 

Je reste à votre disposition pour en échanger de vive voix.

Bien cordialement,`,
  },
  {
    id: "relance", label: "Relance",
    subject: "Relance — votre projet",
    body:
`Bonjour {client},

Je reviens vers vous concernant ma précédente demande, restée sans réponse à ce jour.

Pourriez-vous me confirmer la suite à donner ?

Merci par avance,`,
  },
  {
    id: "accuse", label: "Accusé de réception",
    subject: "Bien reçu — nous traitons votre demande",
    body:
`Bonjour {client},

Nous avons bien reçu votre demande et la traitons dans les meilleurs délais.

Nous revenons vers vous très prochainement.

Cordialement,`,
  },
];

function esc(s) {
  return (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function textToHtml(s) {
  return esc(s).replace(/\n/g, "<br>");
}

const SPAM_WORDS = [
  "gratuit", "100%", "urgent", "gagnez", "gagner", "argent", "cash", "promo", "promotion",
  "offre limitée", "cliquez ici", "cliquez", "félicitations", "félicitation", "gagné", "cadeau",
  "sans engagement", "garanti", "remboursement", "crédit", "prêt", "viagra", "casino", "loterie",
  "free", "winner", "congratulations", "click here", "buy now", "act now", "limited offer",
  "risk-free", "guarantee", "cheap", "discount", "$$$", "bitcoin", "crypto",
];

// Analyse anti-spam heuristique (0 = sûr, 100 = risque élevé)
export function computeSpamScore(subject = "", text = "", html = "") {
  const reasons = [];
  let score = 0;
  const subj = (subject || "").trim();
  const body = (text || "").trim();
  const low = (subj + " " + body).toLowerCase();

  if (!subj) { score += 20; reasons.push("Objet manquant"); }
  else if (subj.length > 90) { score += 8; reasons.push("Objet très long"); }

  // Mots déclencheurs
  const hits = SPAM_WORDS.filter((w) => low.includes(w));
  if (hits.length) {
    score += Math.min(35, hits.length * 9);
    reasons.push(`Mots à risque : ${hits.slice(0, 4).join(", ")}${hits.length > 4 ? "…" : ""}`);
  }

  // MAJUSCULES excessives
  const letters = (subj + body).replace(/[^A-Za-zÀ-ÿ]/g, "");
  const upper = (subj + body).replace(/[^A-ZÀ-Þ]/g, "");
  if (letters.length > 15 && upper.length / letters.length > 0.4) {
    score += 15; reasons.push("Trop de MAJUSCULES");
  }
  if (/[A-ZÀ-Þ]{5,}/.test(subj)) { score += 8; reasons.push("Mot en capitales dans l'objet"); }

  // Ponctuation excessive
  const excl = (subj + body).match(/!/g)?.length || 0;
  if (excl >= 3) { score += 10; reasons.push("Trop de points d'exclamation"); }
  if (/[!?]{2,}/.test(subj + body)) { score += 6; reasons.push("Ponctuation répétée (!!!, ???)"); }

  // Liens
  const links = (html || body).match(/https?:\/\/[^\s"'<)]+/gi) || [];
  const wordCount = body.split(/\s+/).filter(Boolean).length || 1;
  if (links.length >= 4) { score += 12; reasons.push(`Beaucoup de liens (${links.length})`); }
  if (wordCount < 15 && links.length >= 1) { score += 10; reasons.push("Peu de texte pour un email avec lien"); }

  // Corps vide / très court
  if (wordCount < 5) { score += 15; reasons.push("Message très court"); }

  // Symboles monétaires
  if (/(€|\$|£)\s?\d/.test(body) && hits.length) { score += 6; reasons.push("Montant + langage commercial"); }

  score = Math.max(0, Math.min(100, Math.round(score)));
  let level = "faible";
  if (score >= 60) level = "élevé";
  else if (score >= 30) level = "moyen";
  return { score, level, reasons };
}

export function EmailComposer({ initial, users = [], onClose, onSent }) {
  const { user } = useAuth();
  const { managed, selectedId } = useCompany();
  const editorRef = useRef(null);
  const [to, setTo] = useState(initial.to || "");
  const [showToSuggest, setShowToSuggest] = useState(false);
  const [cc, setCc] = useState(initial.cc || "");
  const [showCc, setShowCc] = useState(!!initial.cc);
  const [subject, setSubject] = useState(initial.subject || "");
  const [companyId, setCompanyId] = useState(initial.company_id || (selectedId && selectedId !== "all" ? selectedId : managed[0]?.id || ""));
  const [includeSig, setIncludeSig] = useState(true);
  const [showStudio, setShowStudio] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showColors, setShowColors] = useState(false);
  const [sending, setSending] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [dbTemplates, setDbTemplates] = useState([]);
  const [attachments, setAttachments] = useState(initial.attachments || []);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [accounts, setAccounts] = useState([]);
  const [fromAccount, setFromAccount] = useState("");
  const [bodyText, setBodyText] = useState(initial.body || "");
  const [bodyHtml, setBodyHtml] = useState("");
  const spam = useMemo(() => computeSpamScore(subject, bodyText, bodyHtml), [subject, bodyText, bodyHtml]);

  useEffect(() => {
    api.get("/email-accounts").then(({ data }) => {
      const active = data.filter((a) => a.active);
      setAccounts(active);
      if (active.length) setFromAccount((prev) => prev || active[0].id);
    }).catch(() => {});
  }, []);

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments((prev) => [...prev, data]);
      }
      toast.success("Pièce(s) jointe(s) ajoutée(s)");
    } catch { toast.error("Échec du téléversement"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const removeAtt = (id) => setAttachments((prev) => prev.filter((a) => a.id !== id));

  const company = managed.find((c) => c.id === companyId) || null;

  useEffect(() => {
    if (!companyId) { setDbTemplates([]); return; }
    api.get(`/email-templates?company_id=${companyId}`).then((r) => setDbTemplates(r.data || [])).catch(() => setDbTemplates([]));
  }, [companyId]);

  const sigHtml = useCallback(
    () => buildSignatureHtml(user, company || {}),
    [user, company]
  );

  // init editor content once
  useEffect(() => {
    if (!editorRef.current) return;
    const base = initial.body_html || (initial.body ? textToHtml(initial.body) : "");
    editorRef.current.innerHTML = base + (includeSig ? "<br><br>" + sigHtml() : "");
    setBodyText(editorRef.current.innerText || "");
    setBodyHtml(editorRef.current.innerHTML || "");
    // eslint-disable-next-line
  }, []);

  // re-sync signature block when toggled / company / user / company data changes
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const existing = el.querySelector('[data-fd-sig="1"]');
    if (existing) existing.remove();
    if (includeSig) {
      const wrap = document.createElement("div");
      wrap.innerHTML = "<br><br>" + sigHtml();
      el.appendChild(wrap);
    }
    // eslint-disable-next-line
  }, [includeSig, companyId, user, company]);

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
  };

  const addLink = () => {
    const url = window.prompt("URL du lien :", "https://");
    if (url) exec("createLink", url);
  };

  const applyTemplate = (t) => {
    const vals = {
      client: (to || "").split("@")[0] || "client",
      entreprise: company?.name || "notre équipe",
      moi: user?.name || "",
      tache: "", nom_dossier: "", lien_dossier: "", mot_de_passe: "", date: new Date().toLocaleDateString("fr-FR"),
    };
    const fill = (str) => {
      let out = str || "";
      Object.entries(vals).forEach(([k, v]) => { out = out.split(`{{${k}}}`).join(v).split(`{${k}}`).join(v); });
      return out;
    };
    const body = fill(t.body);
    if (t.subject) setSubject(fill(t.subject));
    if (editorRef.current) {
      editorRef.current.innerHTML = textToHtml(body) + (includeSig ? "<br><br>" + sigHtml() : "");
      setBodyText(editorRef.current.innerText || "");
      setBodyHtml(editorRef.current.innerHTML || "");
    }
    setShowTemplates(false);
    toast.success("Modèle inséré");
  };

  const send = async () => {
    if (!to || !subject) { toast.error("Destinataire et objet requis"); return; }
    const html = editorRef.current?.innerHTML || "";
    const text = editorRef.current?.innerText || "";
    setSending(true);
    try {
      await api.post("/emails", {
        to_email: to, subject, body: text, body_html: html,
        cc: cc || null, in_reply_to: initial.in_reply_to || null,
        company_id: companyId || null, attachments,
        from_account_id: fromAccount || null,
      });
      toast.success(fromAccount ? "Email envoyé ✓" : "Message enregistré (interne)");
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const node = (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-6" data-testid="email-composer">
      <div className={`bg-card border border-white/10 w-full sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden ${minimized ? "max-w-md h-14" : "max-w-3xl h-[92vh] sm:h-[88vh]"}`}>
        {/* Header */}
        <div className="h-14 shrink-0 flex items-center justify-between px-4 border-b border-white/10 bg-background">
          <div className="flex items-center gap-2 font-cabinet font-bold">
            <PenLine size={18} className="text-klein" /> {initial.in_reply_to ? "Répondre" : "Nouveau message"}
          </div>
          <div className="flex items-center gap-1">
            <button data-testid="composer-minimize" onClick={() => setMinimized((m) => !m)} className="p-2 rounded-md hover:bg-white/[0.06] text-zinc-400">
              {minimized ? <Maximize2 size={16} /> : <Minus size={16} />}
            </button>
            <button data-testid="composer-close" onClick={onClose} className="p-2 rounded-md hover:bg-white/[0.06] text-zinc-400"><X size={18} /></button>
          </div>
        </div>

        {!minimized && (
          <>
            {/* Recipients */}
            <div className="px-4 pt-3 space-y-2 shrink-0">
              {accounts.length > 0 && (
                <div className="flex items-center gap-2 border-b border-white/10 pb-2">
                  <span className="text-xs text-zinc-500 w-16 shrink-0">De</span>
                  <select data-testid="composer-from" value={fromAccount} onChange={(e) => setFromAccount(e.target.value)} className="flex-1 bg-transparent text-sm focus:outline-none">
                    {accounts.map((a) => <option key={a.id} value={a.id}>{a.email}</option>)}
                  </select>
                </div>
              )}
              <div className="relative flex items-center gap-2 border-b border-white/10 pb-2">
                <span className="text-xs text-zinc-500 w-16 shrink-0">À</span>
                <input data-testid="composer-to" value={to} autoComplete="off"
                  onChange={(e) => { setTo(e.target.value); setShowToSuggest(true); }}
                  onFocus={() => setShowToSuggest(true)}
                  onBlur={() => setTimeout(() => setShowToSuggest(false), 150)}
                  placeholder="destinataire@email.com"
                  className="flex-1 bg-transparent text-sm focus:outline-none" />
                {!showCc && <button onClick={() => setShowCc(true)} className="text-xs text-zinc-400 hover:text-klein">Cc</button>}
                {showToSuggest && (() => {
                  const q = to.trim().toLowerCase();
                  const matches = users
                    .filter((u) => u.email && (!q || u.email.toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q)) && u.email.toLowerCase() !== q)
                    .slice(0, 6);
                  if (matches.length === 0) return null;
                  return (
                    <div data-testid="composer-to-suggestions" className="absolute left-16 right-0 top-full mt-1 z-30 bg-card border border-white/10 rounded-lg py-1 shadow-2xl max-h-56 overflow-y-auto">
                      {matches.map((u) => (
                        <button key={u.id} type="button" data-testid={`composer-to-suggest-${u.email}`}
                          onMouseDown={(e) => { e.preventDefault(); setTo(u.email); setShowToSuggest(false); }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-white/[0.06]">
                          <span className="w-7 h-7 rounded-full bg-klein/15 text-klein flex items-center justify-center text-[11px] font-bold shrink-0">{(u.name || u.email).slice(0, 1).toUpperCase()}</span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm truncate">{u.name || u.email}</span>
                            <span className="block text-[11px] text-zinc-500 truncate">{u.email}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>
              {showCc && (
                <div className="flex items-center gap-2 border-b border-white/10 pb-2">
                  <span className="text-xs text-zinc-500 w-16 shrink-0">Cc</span>
                  <input data-testid="composer-cc" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="copie@email.com"
                    className="flex-1 bg-transparent text-sm focus:outline-none" />
                </div>
              )}
              <div className="flex items-center gap-2 border-b border-white/10 pb-2">
                <span className="text-xs text-zinc-500 w-16 shrink-0">Objet</span>
                <input data-testid="composer-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Objet du message"
                  className="flex-1 bg-transparent text-sm focus:outline-none font-medium" />
              </div>
            </div>

            {/* Toolbar */}
            <div className="px-3 py-2 flex items-center gap-0.5 flex-wrap shrink-0 border-b border-white/10">
              <ToolBtn t="Gras" onClick={() => exec("bold")}><Bold size={15} /></ToolBtn>
              <ToolBtn t="Italique" onClick={() => exec("italic")}><Italic size={15} /></ToolBtn>
              <ToolBtn t="Souligné" onClick={() => exec("underline")}><Underline size={15} /></ToolBtn>
              <div className="w-px h-5 bg-white/10 mx-1" />
              <ToolBtn t="Liste à puces" onClick={() => exec("insertUnorderedList")}><List size={15} /></ToolBtn>
              <ToolBtn t="Liste numérotée" onClick={() => exec("insertOrderedList")}><ListOrdered size={15} /></ToolBtn>
              <ToolBtn t="Lien" onClick={addLink}><Link2 size={15} /></ToolBtn>
              <div className="relative">
                <ToolBtn t="Couleur" onClick={() => setShowColors((s) => !s)}><Palette size={15} /></ToolBtn>
                {showColors && (
                  <div className="absolute top-full left-0 mt-1 z-10 bg-background border border-white/10 rounded-lg p-2 flex gap-1.5 shadow-xl">
                    {COLORS.map((c) => (
                      <button key={c} onClick={() => { exec("foreColor", c); setShowColors(false); }} className="w-5 h-5 rounded-full border border-white/20" style={{ background: c }} />
                    ))}
                  </div>
                )}
              </div>
              <ToolBtn t="Effacer la mise en forme" onClick={() => exec("removeFormat")}><Eraser size={15} /></ToolBtn>
              <div className="w-px h-5 bg-white/10 mx-1" />
              <input ref={fileRef} type="file" multiple className="hidden" onChange={onPickFiles} data-testid="composer-file-input" />
              <ToolBtn t="Joindre un fichier" onClick={() => fileRef.current?.click()}>{uploading ? <Loader2 size={15} className="animate-spin" /> : <Paperclip size={15} />}</ToolBtn>
              <div className="w-px h-5 bg-white/10 mx-1" />
              <div className="relative">
                <button data-testid="composer-templates" onClick={() => setShowTemplates((s) => !s)} className="flex items-center gap-1 px-2 py-1.5 rounded-md hover:bg-white/[0.06] text-zinc-300 text-xs font-medium">
                  <FileText size={14} /> Modèles <ChevronDown size={13} />
                </button>
                {showTemplates && (
                  <div className="absolute top-full left-0 mt-1 z-10 bg-background border border-white/10 rounded-lg py-1 w-60 shadow-xl max-h-72 overflow-y-auto">
                    {dbTemplates.length > 0 && (
                      <>
                        <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-zinc-500">Modèles de l'entreprise</div>
                        {dbTemplates.map((t) => (
                          <button key={t.id} data-testid={`template-db-${t.id}`} onClick={() => applyTemplate(t)} className="w-full text-left px-3 py-2 text-sm hover:bg-white/[0.06] flex items-center gap-2">
                            <FileText size={14} className="text-klein shrink-0" /> <span className="truncate">{t.name}</span>
                          </button>
                        ))}
                        <div className="h-px bg-white/10 my-1" />
                        <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-zinc-500">Modèles rapides</div>
                      </>
                    )}
                    {TEMPLATES.map((t) => (
                      <button key={t.id} data-testid={`template-${t.id}`} onClick={() => applyTemplate(t)} className="w-full text-left px-3 py-2 text-sm hover:bg-white/[0.06] flex items-center gap-2">
                        <FileText size={14} className="text-klein" /> {t.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Editor */}
            <div className="flex-1 overflow-y-auto px-4 py-4 bg-background">
              <div ref={editorRef} data-testid="composer-body" contentEditable suppressContentEditableWarning data-placeholder="Votre message…"
                onInput={(e) => { setBodyText(e.currentTarget.innerText || ""); setBodyHtml(e.currentTarget.innerHTML || ""); }}
                className="fd-editor fd-editor-paper bg-white text-zinc-900 rounded-xl p-6 min-h-full outline-none text-[15px] leading-relaxed shadow-[0_1px_3px_rgba(16,24,40,0.1)]" />
            </div>

            {/* Attachments */}
            {attachments.length > 0 && (
              <div className="px-4 py-2 border-t border-white/10 bg-background shrink-0 flex flex-wrap gap-2" data-testid="composer-attachments">
                {attachments.map((a) => (
                  <div key={a.id} className="flex items-center gap-2 pl-2 pr-1 py-1 rounded-lg bg-card border border-white/10 text-xs max-w-[220px]">
                    <FileIcon size={13} className="text-klein shrink-0" />
                    <span className="truncate" title={a.name}>{a.name}</span>
                    <span className="text-zinc-500 shrink-0">{fmtSize(a.size)}</span>
                    <button data-testid={`composer-att-remove-${a.id}`} onClick={() => removeAtt(a.id)} className="p-0.5 rounded hover:bg-white/10 text-zinc-400 shrink-0"><X size={13} /></button>
                  </div>
                ))}
              </div>
            )}

            {/* Signature controls */}
            <div className="px-4 py-2 border-t border-white/10 bg-background shrink-0">
              <div className="flex items-center gap-3 flex-wrap">
                <label className="flex items-center gap-1.5 text-xs text-zinc-400 cursor-pointer">
                  <input type="checkbox" data-testid="composer-include-sig" checked={includeSig} onChange={(e) => setIncludeSig(e.target.checked)} className="accent-klein" />
                  Signature
                </label>
                {managed.length > 0 && (
                  <select data-testid="composer-company" value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="text-xs bg-card border border-white/10 rounded-md px-2 py-1">
                    {managed.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                )}
                <button data-testid="composer-edit-sig" onClick={() => setShowStudio(true)} className="text-xs text-klein hover:underline flex items-center gap-1">
                  <ImageIcon size={12} /> Studio de signature
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-white/10 flex items-center justify-between shrink-0 bg-card">
              <button onClick={onClose} className="px-4 py-2 rounded-md border border-white/10 text-sm font-medium hover:bg-white/[0.06]">Annuler</button>
              <div className="flex items-center gap-3">
                <SpamBadge spam={spam} />
                <button data-testid="send-email-button" onClick={send} disabled={sending} className="flex items-center gap-2 px-5 py-2 rounded-md bg-klein text-zinc-950 text-sm font-medium hover:opacity-90">
                  <Send size={15} /> {sending ? "Envoi…" : "Envoyer"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      {showStudio && <SignatureStudio companyId={companyId} onClose={() => setShowStudio(false)} onSaved={() => {}} />}
    </div>
  );

  return createPortal(node, document.body);
}

function SpamBadge({ spam }) {
  const [open, setOpen] = useState(false);
  const cfg = {
    faible: { cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", Icon: ShieldCheck, label: "Risque spam faible" },
    moyen: { cls: "bg-amber-500/15 text-amber-400 border-amber-500/30", Icon: ShieldAlert, label: "Risque spam moyen" },
    "élevé": { cls: "bg-red-500/15 text-red-400 border-red-500/30", Icon: ShieldX, label: "Risque spam élevé" },
  }[spam.level];
  const Icon = cfg.Icon;
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" data-testid="spam-score-badge" onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 px-2.5 h-9 rounded-md border text-xs font-medium ${cfg.cls}`}>
        <Icon size={14} /> {spam.score}/100
      </button>
      {open && (
        <div data-testid="spam-score-detail" className="absolute bottom-full right-0 mb-2 w-64 bg-background border border-white/10 rounded-lg p-3 shadow-xl z-20">
          <div className="text-xs font-semibold mb-1.5">{cfg.label} — {spam.score}/100</div>
          {spam.reasons.length ? (
            <ul className="text-[11px] text-zinc-400 space-y-1 list-disc pl-4">
              {spam.reasons.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          ) : (
            <p className="text-[11px] text-zinc-400">Aucun signal de spam détecté. Cet email devrait bien arriver en boîte de réception.</p>
          )}
        </div>
      )}
    </div>
  );
}

function ToolBtn({ children, onClick, t }) {
  return (
    <button type="button" title={t} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      className="p-1.5 rounded-md hover:bg-white/[0.06] text-zinc-300 transition-colors">
      {children}
    </button>
  );
}

export function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}
